import { px } from '@adsk/alloy-react-helpers';
import {
  TRANSITION_DURATION,
  TRANSITION_EASING,
} from '@adsk/alloy-react-theme';

import {
  AlloyLegacyThemeDesignTokens,
  FileVersionIndicatorDesignTokens,
} from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

const PILL_HEIGHT = '24px';
const PILL_MIN_WIDTH = '36px';
const HANGING_ICON_SIZE = '16px';

export const getFileVersionIndicatorTokensAlloy = (
  alloyTheme: AlloyLegacyThemeDesignTokens,
): FileVersionIndicatorDesignTokens => {
  const { typography, spacing, borderRadius, halos, colors } = alloyTheme;
  const { colors: tokenColors } = alloyTheme.tokens;

  return {
    pill: {
      height: PILL_HEIGHT,
      'min-width': PILL_MIN_WIDTH,
      'border-radius': px(borderRadius.pill),
      'padding-left': px(spacing.xSmall),
      'padding-right': px(spacing.xSmall),
      'padding-top': '0',
      'padding-bottom': '0',
      'font-size': typography.labelMedium.fontSize,
      'font-weight': typography.labelMedium.fontWeight,
      'line-height': typography.labelMedium.lineHeight,
      'font-family': typography.labelMedium.fontFamily,
      default: {
        'background-color': tokenColors.background.primary.value,
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': tokenColors.border.value,
        'text-color': tokenColors.text.primary.value,
      },
      clickable: {
        'background-color': tokenColors.background.secondary.value,
        'text-color': tokenColors.text.link.value,
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': tokenColors.border.value,
        hover: {
          'text-color': tokenColors.text.hover.value,
        },
        active: {
          'text-color': tokenColors.text.pressed.value,
        },
        disabled: {
          'text-color': tokenColors.text.inactive.value,
        },
        interactive: {
          transition: `box-shadow ${TRANSITION_DURATION}ms ${TRANSITION_EASING}, border-color ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`,
          hover: {
            'box-shadow': halos.hover,
          },
          focus: {
            'box-shadow': halos.focus,
          },
          active: {
            'box-shadow': halos.active,
            'border-color': colors.charcoal500,
          },
          disabled: {
            'box-shadow': 'none',
            cursor: 'not-allowed',
          },
        },
      },
    },
    'status-badge': {
      'margin-left': px(spacing.xSmall),
      'padding-left': px(spacing.xSmall),
      'padding-right': px(spacing.xSmall),
      'padding-top': '0',
      'padding-bottom': '0',
      'border-radius': px(borderRadius.pill),
      'font-size': typography.labelMedium.fontSize,
      'font-weight': typography.labelMedium.fontWeight,
      'line-height': typography.labelMedium.lineHeight,
      new: {
        'background-color': tokenColors.background.new.subtler.value,
        'text-color': tokenColors.text.primary.value,
      },
      'not-current': {
        'background-color': tokenColors.background.warning.subtler.value,
        'text-color': tokenColors.text.primary.value,
      },
    },
    'hanging-icon': {
      bottom: '-5px',
      right: '-5px',
      size: HANGING_ICON_SIZE,
      'border-width': '1px',
      'border-style': 'solid',
      'border-color': tokenColors.background.primary.value,
      'border-radius': borderRadius.circle,
      new: {
        'background-color': colors.turquoise500,
        'icon-color': tokenColors.background.primary.value,
        'icon-size': '10px',
        padding: '4px',
      },
      'not-current': {
        'background-color': colors.yellowOrange500,
        'icon-color': tokenColors.background.primary.value,
        'icon-size': '16px',
        padding: '0',
      },
    },
    container: {
      full: {
        gap: '0',
      },
      condensed: {
        position: 'relative',
      },
    },
  };
};

export const getFileVersionIndicatorTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): FileVersionIndicatorDesignTokens => {
  const semantic = tokens?.semantic;
  const badge = tokens?.component?.badge;
  const generic = tokens?.generic;

  const pillBorder = {
    borderWidth: '1px',
    borderStyle: 'solid' as const,
    borderColor: semantic?.['border-color']?.medium?.default,
  };

  return {
    pill: {
      height: PILL_HEIGHT,
      'min-width': PILL_MIN_WIDTH,
      'border-radius': badge?.['border-radius'],
      'padding-left': badge?.text?.['padding-left'],
      'padding-right': badge?.text?.['padding-right'],
      'padding-top': badge?.text?.['padding-top'],
      'padding-bottom': badge?.text?.['padding-bottom'],
      'font-size': badge?.['font-size'],
      'font-weight': badge?.['font-weight'],
      'line-height': badge?.['line-height'],
      'font-family': fontFamily,
      default: {
        'background-color': semantic?.['background-color']?.surface?.[100],
        'border-width': pillBorder.borderWidth,
        'border-style': pillBorder.borderStyle,
        'border-color': pillBorder.borderColor,
        'text-color': semantic?.['text-color']?.medium?.default,
      },
      clickable: {
        'background-color': semantic?.['background-color']?.surface?.[200],
        'text-color': semantic?.['text-color']?.link?.default,
        'border-width': pillBorder.borderWidth,
        'border-style': pillBorder.borderStyle,
        'border-color': pillBorder.borderColor,
        hover: {
          'text-color': generic?.color?.blue?.[500]?.[100],
        },
        active: {
          'text-color': generic?.color?.blue?.[600]?.[100],
        },
        disabled: {
          'text-color': generic?.color?.charcoal?.[500]?.[100],
        },
        interactive: {
          transition: undefined,
          hover: undefined,
          focus: {
            'box-shadow': semantic?.halo?.default?.focus,
          },
          active: undefined,
          disabled: {
            cursor: 'not-allowed',
          },
        },
      },
    },
    'status-badge': {
      'margin-left': generic?.spacing?.[8],
      'padding-left': badge?.text?.['padding-left'],
      'padding-right': badge?.text?.['padding-right'],
      'padding-top': badge?.text?.['padding-top'],
      'padding-bottom': badge?.text?.['padding-bottom'],
      'border-radius': badge?.['border-radius'],
      'font-size': badge?.['font-size'],
      'font-weight': badge?.['font-weight'],
      'line-height': badge?.['line-height'],
      new: {
        'background-color': generic?.color?.turquoise?.[100]?.[100],
        'text-color': semantic?.['text-color']?.['on-status']?.light,
      },
      'not-current': {
        'background-color': generic?.color?.orange?.[100]?.[100],
        'text-color': semantic?.['text-color']?.['on-status']?.light,
      },
    },
    'hanging-icon': {
      bottom: '-5px',
      right: '-5px',
      size: HANGING_ICON_SIZE,
      'border-width': '1px',
      'border-style': 'solid',
      'border-color': semantic?.['background-color']?.surface?.[100],
      'border-radius': '50%',
      new: {
        'background-color': generic?.color?.turquoise?.[500]?.[100],
        'icon-color': generic?.color?.white?.[100],
        'icon-size': '10px',
        padding: '4px',
      },
      'not-current': {
        'background-color': generic?.color?.orange?.[500]?.[100],
        'icon-color': generic?.color?.white?.[100],
        'icon-size': '16px',
        padding: '0',
      },
    },
    container: {
      full: {
        gap: generic?.spacing?.[8],
      },
      condensed: {
        position: 'relative',
      },
    },
  };
};
